import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Bell, Mail, X, ExternalLink, AlertCircle, CheckCircle2 } from "lucide-react";
import {
  useInvoiceReminders,
  useSendReminder,
  useDismissReminder,
  type InvoiceReminder,
} from "@/hooks/useInvoiceReminders";
import { SendReminderDialog } from "./SendReminderDialog";
import { useNavigate } from "react-router-dom";

interface NotificationPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const NotificationPanel = ({ open, onOpenChange }: NotificationPanelProps) => {
  const [selectedReminder, setSelectedReminder] = useState<InvoiceReminder | null>(null);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [dismissReminderId, setDismissReminderId] = useState<string | null>(null);
  const navigate = useNavigate();

  const { data: pendingReminders, isLoading: loadingPending } = useInvoiceReminders("pending");
  const { data: sentReminders, isLoading: loadingSent } = useInvoiceReminders("sent");
  const { data: allReminders, isLoading: loadingAll } = useInvoiceReminders();

  const { mutate: sendReminder, isPending: isSending } = useSendReminder();
  const { mutate: dismissReminder } = useDismissReminder();

  const handleSendClick = (reminder: InvoiceReminder) => {
    setSelectedReminder(reminder);
    setShowSendDialog(true);
  };

  const handleSendConfirm = (reminder_id: string, custom_message?: string) => {
    sendReminder({ reminder_id, custom_message }, {
      onSuccess: () => {
        setShowSendDialog(false);
        setSelectedReminder(null);
      },
    });
  };

  const handleDismissClick = (reminder_id: string) => {
    setDismissReminderId(reminder_id);
  };

  const handleDismissConfirm = () => {
    if (dismissReminderId) {
      dismissReminder(dismissReminderId);
      setDismissReminderId(null);
    }
  };

  const handleViewInvoice = (invoiceId: string) => {
    navigate("/invoices");
    onOpenChange(false);
  };

  const ReminderCard = ({ reminder }: { reminder: InvoiceReminder }) => {
    const invoice = reminder.invoices;
    const client = invoice?.clients;
    const dueDate = new Date(reminder.due_date);
    const isPending = reminder.status === "pending";
    const isSent = reminder.status === "sent";

    return (
      <div className="rounded-lg border bg-card p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1 flex-1">
            <div className="flex items-center gap-2">
              {isPending && <AlertCircle className="h-4 w-4 text-warning" />}
              {isSent && <CheckCircle2 className="h-4 w-4 text-success" />}
              <h4 className="font-semibold">
                Invoice #{invoice?.invoice_number}
              </h4>
            </div>
            <p className="text-sm text-muted-foreground">
              {client?.company_name || client?.name}
            </p>
          </div>
          <Badge variant={isPending ? "destructive" : "secondary"}>
            {isPending
              ? `Due in ${reminder.days_until_due} days`
              : isSent
              ? "Sent"
              : reminder.status}
          </Badge>
        </div>

        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Due Date:</span>
            <span className="font-medium">
              {dueDate.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Amount:</span>
            <span className="font-medium">
              ₹{invoice?.total?.toLocaleString('en-IN')}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Client Email:</span>
            <span className="font-medium truncate max-w-[180px]">
              {client?.email || reminder.recipient_email}
            </span>
          </div>
          {isSent && reminder.sent_at && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Sent At:</span>
              <span className="font-medium">
                {new Date(reminder.sent_at).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          {isPending && (
            <>
              <Button
                size="sm"
                onClick={() => handleSendClick(reminder)}
                className="flex-1"
              >
                <Mail className="mr-2 h-4 w-4" />
                Send Reminder
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDismissClick(reminder.id)}
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleViewInvoice(reminder.invoice_id)}
            className={isPending ? "" : "flex-1"}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            View Invoice
          </Button>
        </div>
      </div>
    );
  };

  const EmptyState = ({ message }: { message: string }) => (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Bell className="h-12 w-12 text-muted-foreground/50 mb-4" />
      <p className="text-muted-foreground">{message}</p>
    </div>
  );

  const LoadingSkeleton = () => (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="rounded-lg border p-4 space-y-3">
          <div className="flex items-start justify-between">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-5 w-20" />
          </div>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <div className="flex gap-2">
            <Skeleton className="h-9 flex-1" />
            <Skeleton className="h-9 w-9" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Invoice Reminders</SheetTitle>
            <SheetDescription>
              Manage payment reminder notifications for your invoices
            </SheetDescription>
          </SheetHeader>

          <Tabs defaultValue="pending" className="mt-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="pending" className="relative">
                Pending
                {(pendingReminders?.length || 0) > 0 && (
                  <Badge
                    variant="destructive"
                    className="ml-2 h-5 min-w-5 px-1 text-xs"
                  >
                    {pendingReminders?.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="sent">
                Sent
                {(sentReminders?.length || 0) > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 min-w-5 px-1 text-xs">
                    {sentReminders?.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>

            <TabsContent value="pending" className="space-y-4 mt-4">
              {loadingPending ? (
                <LoadingSkeleton />
              ) : !pendingReminders || pendingReminders.length === 0 ? (
                <EmptyState message="No pending reminders. You're all caught up!" />
              ) : (
                pendingReminders.map((reminder) => (
                  <ReminderCard key={reminder.id} reminder={reminder} />
                ))
              )}
            </TabsContent>

            <TabsContent value="sent" className="space-y-4 mt-4">
              {loadingSent ? (
                <LoadingSkeleton />
              ) : !sentReminders || sentReminders.length === 0 ? (
                <EmptyState message="No sent reminders yet" />
              ) : (
                sentReminders.map((reminder) => (
                  <ReminderCard key={reminder.id} reminder={reminder} />
                ))
              )}
            </TabsContent>

            <TabsContent value="all" className="space-y-4 mt-4">
              {loadingAll ? (
                <LoadingSkeleton />
              ) : !allReminders || allReminders.length === 0 ? (
                <EmptyState message="No reminders found" />
              ) : (
                allReminders.map((reminder) => (
                  <ReminderCard key={reminder.id} reminder={reminder} />
                ))
              )}
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>

      <SendReminderDialog
        reminder={selectedReminder}
        open={showSendDialog}
        onOpenChange={setShowSendDialog}
        onConfirm={handleSendConfirm}
        isLoading={isSending}
      />

      <AlertDialog
        open={!!dismissReminderId}
        onOpenChange={(open) => !open && setDismissReminderId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dismiss Reminder?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the reminder from your pending list. You can still
              send it manually from the invoice details page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDismissConfirm}>
              Dismiss
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};